/**
 * agreement-arc-index.ts — finding the in-app document that stands for a legacy advance row.
 *
 * PURE MODULE: no fetch, no hooks. `useAgreementArcIndex` does the reads; the join is here.
 *
 * ── THE PROBLEM THIS SOLVES ───────────────────────────────────────────────────
 * The contract insight tabs offer "View Agreement" on an advance, and what they open is
 * `loans[].agreement.agreement_url` / `insurances[].loan.agreement_url` — a storage address.
 * For an advance signed IN THE APP that address is the `generated/` object `agreement_finalize`
 * wrote back onto the legacy row, and fleet-backend is about to make `generated/` a reserved
 * prefix no driver may read. Left alone, those three buttons become three dead ends.
 *
 * The replacement is the in-app viewer, which needs a DOCUMENT ID rather than a URL. Nothing
 * in the insights payload carries one, and `get_agreement_documents` projects no arc columns
 * — so the join is made here, out of two reads a driver is already entitled to:
 *
 *   agreement_documents        `loan_agreement_id` / `insurance_loan_agreement_id`, the
 *                              exclusive arc naming the legacy row the document was raised
 *                              against (RLS admits the contract's parties).
 *   loan_agreements            `id -> loan_id`, which is what the insights payload keys its
 *   insurance_loan_agreements  advances by (`loans[].id`, `insurances[].loan.id`).
 *
 * ── AN UNMATCHED ADVANCE IS A PAPER ONE, AND KEEPS ITS SCAN ───────────────────
 * The same reasoning `unifyAgreementDocuments` uses for the contract and deposit arcs: a
 * legacy row whose arc holds no live in-app document was signed on paper, its `agreement_url`
 * names an ordinary uploaded scan rather than a `generated/` object, and that scan stays
 * readable. Hiding it would take an agreement away from every driver whose advance predates
 * in-app signing — which is all of them in production — for no security gain at all.
 *
 * ── AND AN EMPTY INDEX IS SAFE ────────────────────────────────────────────────
 * Every read behind this degrades to nothing rather than throwing, so an index that could not
 * be built leaves the tabs exactly as they are today. It cannot invent a document, and it
 * cannot be the reason a driver sees a button that goes nowhere.
 */

import { holdsArc } from '@/lib/esign';

/** One `agreement_documents` row, as the arc join reads it. */
export interface ArcDocumentRow {
    id: number;
    status: string;
    loan_agreement_id: number | null;
    insurance_loan_agreement_id: number | null;
}

/** One legacy agreement row: its own id, and the advance it belongs to. */
export interface LegacyArcRow {
    id: number;
    loan_id: number;
}

export interface AgreementArcIndex {
    /** `loans.id` -> in-app document id. */
    loans: ReadonlyMap<number, number>;
    /** `insurance_loans.id` -> in-app document id. */
    insuranceLoans: ReadonlyMap<number, number>;
}

export const EMPTY_ARC_INDEX: AgreementArcIndex = {
    loans: new Map<number, number>(),
    insuranceLoans: new Map<number, number>(),
};

export interface AgreementArcIndexInput {
    documents: readonly ArcDocumentRow[] | null | undefined;
    loanAgreements: readonly LegacyArcRow[] | null | undefined;
    insuranceLoanAgreements: readonly LegacyArcRow[] | null | undefined;
}

function byId(rows: readonly LegacyArcRow[] | null | undefined): Map<number, number> {
    const out = new Map<number, number>();
    for (const row of rows ?? []) {
        if (typeof row?.id === 'number' && typeof row?.loan_id === 'number') {
            out.set(row.id, row.loan_id);
        }
    }
    return out;
}

export function buildAgreementArcIndex(input: AgreementArcIndexInput): AgreementArcIndex {
    const loanIdByAgreement = byId(input.loanAgreements);
    const insuranceLoanIdByAgreement = byId(input.insuranceLoanAgreements);

    const loans = new Map<number, number>();
    const insuranceLoans = new Map<number, number>();

    for (const document of input.documents ?? []) {
        // A withdrawn or replaced document RELEASES its arc — the legacy row underneath it is
        // free to be signed on paper again, and the admin board offers exactly that. The same
        // denylist the partial unique index behind every arc is keyed on.
        if (!holdsArc(document)) continue;

        if (typeof document.loan_agreement_id === 'number') {
            const loanId = loanIdByAgreement.get(document.loan_agreement_id);
            if (typeof loanId === 'number') loans.set(loanId, document.id);
            continue;
        }
        if (typeof document.insurance_loan_agreement_id === 'number') {
            const loanId = insuranceLoanIdByAgreement.get(document.insurance_loan_agreement_id);
            if (typeof loanId === 'number') insuranceLoans.set(loanId, document.id);
        }
    }

    return { loans, insuranceLoans };
}

/**
 * What an advance's single agreement affordance should DO, on the contract insight tabs.
 *
 * Three answers and no fourth:
 *
 *   viewer  an in-app document holds this advance's arc, so the agreement is read at
 *           `/agreement/:documentId` — the executed PDF's storage address is about to stop
 *           resolving for a driver, and the viewer is the route that does not need one.
 *   scan    no in-app document, but a legacy `agreement_url`. That is a PAPER signing, filed
 *           by an admin at an ordinary object name, and it stays readable exactly as it is
 *           today — the same carve-out the "View scan" card on the Documents page keeps.
 *   null    neither. No button, rather than a button that goes nowhere.
 *
 * THE IN-APP DOCUMENT WINS WHEREVER THERE IS ONE, even though the legacy row usually still
 * carries a URL: `agreement_finalize` writes the executed PDF's `generated/` path back onto
 * that row, so the two are the SAME instrument seen through two reads, and only one of them
 * is a route that will keep working.
 */
export type AdvanceAgreementTarget =
    | { kind: 'viewer'; documentId: number }
    | { kind: 'scan'; url: string }
    | null;

export function advanceAgreementTarget(
    documentId: number | null | undefined,
    scanUrl: string | null | undefined,
): AdvanceAgreementTarget {
    if (typeof documentId === 'number' && Number.isInteger(documentId) && documentId > 0) {
        return { kind: 'viewer', documentId };
    }
    if (typeof scanUrl === 'string' && scanUrl.trim().length > 0) {
        return { kind: 'scan', url: scanUrl };
    }
    return null;
}
